import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { Feature } from '@loyalty/shared-common';
import { QlfNavigationCard, colorPalette } from '@qualifio/ui-commons';
import { useNavigate } from '@qualifio/ui-commons/dist/components/qlf-prompt';
import { useTranslation } from 'react-i18next';
import { DetailForm } from '../../libs/shared/layout/DetailForm';
import { QlfMasterDetailContainer } from '../../libs/shared/layout/QlfMasterDetailContainer';
import { Router } from '../../libs/services';
import {
  currenciesIcon,
  incomingWebhooksIcon,
  levelsIcon,
  programAccessIcon,
  programLoginIcon,
  referralCodesIcon,
} from '../../libs/shared/icons';
import { useAccounts } from '../../libs/accounts/AccountsContext';
import { useFeatures } from '../../libs/features/FeaturesContext';
import { useProgram } from '../../libs/loyalty/ProgramContext';

const sx = {
  card: {
    icon: {
      color: colorPalette.yellow.main,
    },
    card: {
      height: '100%',
      cursor: 'pointer',
      textDecoration: 'none !important',
    },
  },
  titleWithLine: {
    fontSize: 20,
    marginBottom: '20px !important',
    textAlign: 'center',
    '&:after': {
      backgroundColor: colorPalette.primary.main,
      content: "''",
      display: 'block',
      height: 2,
      margin: '10px auto 20px auto',
      width: 40,
    },
  },
  description: {
    marginBottom: '30px !important',
    textAlign: 'center',
  },
  disabled: {
    cursor: 'not-allowed',
  },
  container: {
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: '10%',
  },
  buttons: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '20px',
    width: '60%',
    marginRight: 'auto',
    marginLeft: 'auto',
  },
};

function OptionButton({
  icon,
  title,
  description,
  tooltip,
  handleClick,
  disabled,
}: {
  icon: any;
  title: string;
  description: string;
  tooltip: string;
  handleClick: () => void;
  disabled: boolean;
}): React.ReactElement {
  return (
    <Tooltip title={tooltip}>
      <Box
        sx={disabled ? sx.disabled : undefined}
        onClick={() => {
          if (!disabled) {
            handleClick();
          }
        }}
      >
        <QlfNavigationCard
          icon={icon}
          title={title as any}
          description={description as any}
          sxs={sx.card}
        />
      </Box>
    </Tooltip>
  );
}

export function ProgramSettingsPage() {
  const { t } = useTranslation();
  const { features } = useFeatures();
  const { currentAccount } = useAccounts();
  const { program } = useProgram();
  const accountId = currentAccount.id;
  const programId = program.id;
  const navigate = useNavigate();
  const { read: currencyRead } = features[Feature.CURRENCY];
  const { read: incomingWebhooksRead } = features[Feature.INCOMING_WEBHOOK];
  const { read: referralRead } = features[Feature.REFERRAL];
  const { read: programAccessRead } = features[Feature.PROGRAM_ACCESS];
  const { read: programLoginRead } = features[Feature.PROGRAM_LOGIN];
  const { read: levelsRead } = features[Feature.LEVEL];

  const goToCurrency = () => {
    navigate(Router.getPath('currencies', { accountId, id: programId }));
  };

  const goToIncomingWebhooks = () => {
    navigate(Router.getPath('incoming-webhooks', { accountId, id: programId }));
  };

  const goToReferralCodes = () => {
    navigate(Router.getPath('referral', { accountId, id: programId }));
  };

  const goToProgramAccess = () => {
    navigate(Router.getPath('access', { accountId, id: programId }));
  };

  const goToProgramLogin = () => {
    navigate(Router.getPath('login', { accountId, id: programId }));
  };

  const goToLevels = () => {
    navigate(Router.getPath('levels', { accountId, id: programId }));
  };

  return (
    <QlfMasterDetailContainer>
      <DetailForm>
        <Box sx={sx.container}>
          <Typography sx={sx.titleWithLine} variant="h2">
            {t('loyalty-topbar.actions.settings')}
          </Typography>

          <Typography sx={sx.description} variant="body1">
            {t('program.settings.description')}
          </Typography>

          <Box sx={sx.buttons}>
            <OptionButton
              disabled={!currencyRead}
              tooltip={currencyRead ? '' : t('shared.action.permission.required')}
              icon={currenciesIcon}
              title={t('topbar.tabs.settings.currency')}
              description={t('program.settings.currencies.description')}
              handleClick={goToCurrency}
            />

            <OptionButton
              disabled={!levelsRead}
              tooltip={levelsRead ? '' : t('shared.action.permission.required')}
              icon={levelsIcon}
              title={t('topbar.tabs.settings.levels')}
              description={t('program.settings.levels.description')}
              handleClick={goToLevels}
            />

            <OptionButton
              disabled={!incomingWebhooksRead}
              tooltip={incomingWebhooksRead ? '' : t('shared.action.permission.required')}
              icon={incomingWebhooksIcon}
              title={t('topbar.tabs.settings.incoming-webhook')}
              description={t('program.settings.incoming-webhooks.description')}
              handleClick={goToIncomingWebhooks}
            />

            <OptionButton
              disabled={!referralRead}
              tooltip={referralRead ? '' : t('shared.action.permission.required')}
              icon={referralCodesIcon}
              title={t('topbar.tabs.settings.referral')}
              description={t('program.settings.referrals.description')}
              handleClick={goToReferralCodes}
            />

            <OptionButton
              disabled={!programAccessRead}
              tooltip={programAccessRead ? '' : t('shared.action.permission.required')}
              icon={programAccessIcon}
              title={t('topbar.tabs.settings.access')}
              description={t('program.settings.program-access.description')}
              handleClick={goToProgramAccess}
            />

            <OptionButton
              disabled={!programLoginRead}
              tooltip={programLoginRead ? '' : t('shared.action.permission.required')}
              icon={programLoginIcon}
              title={t('topbar.tabs.settings.login')}
              description={t('program.settings.program-login.description')}
              handleClick={goToProgramLogin}
            />
          </Box>
        </Box>
      </DetailForm>
    </QlfMasterDetailContainer>
  );
}
